import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { useRootStore } from '@/store';
import { Alert } from '@/store/classes';
import FormField from "@/components/common/FormField.vue3.vue"

import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { z } from 'zod';
import { useFormValidation } from './common/FormValidation';
import { RequiredStringSchema } from './common/schemas';
import { useI18n } from 'vue-i18n';
const AUTO_REFRESH_MS = 120000; // Refresh token 2 minutes before it expires if session is not locked


export default /*@__PURE__*/_defineComponent({
  __name: 'InactivityModal.vue3',
  setup(__props) {

const { t } = useI18n();

const router = useRouter();
const rootStore = useRootStore();

const setAccessToken = (token?: string) => rootStore.dispatch('setAccessToken', token);
const setFacilityId = (facilityId?: string) => rootStore.dispatch('setFacilityId', facilityId);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = async (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);
const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const setSessionLocked = (locked: boolean) => rootStore.dispatch('setSessionLocked', locked);
const hideModal = () => rootStore.dispatch('hideModal');
const hideConfirmModal = () => rootStore.dispatch('hideConfirmModal');

const facility = computed(() => rootStore.state.facility);
const accessToken = computed(() => rootStore.state.accessToken);
const sessionTicker = computed(() => rootStore.state.sessionTicker);
const sessionLocked = computed(() => rootStore.state.sessionLocked);

const isStandaloneUserFacility = computed(() => rootStore.getters.isStandaloneUserFacility);

const lastActivity = ref(new Date())
const isRefreshing = ref(false);
const password = ref('');

const previousUserName = ref('');
const previousAppName = ref('');

const refreshActivity = () => lastActivity.value = new Date();

const Schema = z.object({ password: RequiredStringSchema })
const passwordModel = computed(() => ({ password: password.value }))
const validationState = useFormValidation<z.infer<typeof Schema>>(Schema, passwordModel, true);

onMounted(() => {
    refreshActivity();
    document.addEventListener('mousedown', refreshActivity);
    document.addEventListener('touchstart', refreshActivity);
})

watch(sessionTicker, async () => {
    const now = new Date();
    const sessionTimeout = lastActivity?.value.getTime() + facility.value.clientSideTimeout * 60000;
    if (!sessionLocked.value && accessToken && accessToken.value.value && sessionTimeout <= now.getTime()) {
        await lockSession();
    }
    if (!isRefreshing.value && !sessionLocked.value && accessToken.value && accessToken.value.tokenExpiration 
        && accessToken.value.tokenExpiration.getTime() - AUTO_REFRESH_MS <= now.getTime()
        && accessToken.value.tokenExpiration.getTime() > now.getTime()) {
        isRefreshing.value = true;
        await refreshToken();
    }
})

async function confirm() {
    await showLoader('inactivity');
    let hasLogin = false;
    if (!validationState.validate(true)) {
        return;
    }
    try {
        const authResponse = await axios({
            url: isStandaloneUserFacility.value ? '/auth/messaging' : '/auth/sso',
            method: 'post',
            data: {
                username: previousUserName.value,
                password: password.value,
                applicationName: previousAppName.value
            }
        });
        hasLogin = true;
        if (authResponse.data) {
            setAccessToken(authResponse.data);
            setSessionLocked(false);
        }
    } catch (e: any) {
        validationState.result.value.password.invalid = true;
        validationState.result.value.password.submitAttempted = true;
        validationState.result.value.password.message = (e?.response?.status === 401 && e.response.data)
            ? e.response.data
            : hasLogin ? 'Unable to log in, please try again later' : 'The password was incorrect'

    } finally {
        await hideLoader('inactivity');
    }
}

async function lockSession() {
    setSessionLocked(true);
    previousUserName.value = (accessToken.value?.username) ? accessToken.value.username : previousUserName.value;
    previousAppName.value = (accessToken.value?.appName) ? accessToken.value.appName : previousAppName.value;
    await setAccessToken();
}

async function logout() {
    hideModal();
    hideConfirmModal();
    await setAccessToken();
    await setFacilityId();
    setSessionLocked(false);
    router.push({
        path: '/login', 
        query: { facilityType: facility.value.externalFacilityType }
    });
}

async function refreshToken() {
    try {
        const authResponse = await axios.get<string>('/auth');
        if (authResponse && authResponse.data) {
            await setAccessToken(authResponse.data)
        }
    } catch (e) {
        console.error(e);
        showAlert({
            text: 'Failed to automatically renew session. Please reload and log in again.',
            type: 'error'
        });
        await setAccessToken();
    } finally {
        isRefreshing.value = false;
    }
}

return (_ctx: any,_cache: any) => {
  const _component_p_input_text = _resolveComponent("p-input-text")!
  const _component_p_button = _resolveComponent("p-button")!
  const _component_p_dialog = _resolveComponent("p-dialog")!

  return (_openBlock(), _createBlock(_component_p_dialog, {
    visible: sessionLocked.value,
    closable: false,
    header: "Inactivity Warning",
    modal: ""
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_p_button, {
        class: "p-button-outlined",
        onClick: logout
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('actions.logout')), 1)
        ]),
        _: 1
      }),
      _createVNode(_component_p_button, {
        type: "submit",
        onClick: _withModifiers(confirm, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('actions.continue')), 1)
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _cache[1] || (_cache[1] = _createElementVNode("p", null, " You have been inactive for an extended period of time. Please re-enter your password to continue. ", -1)),
      _createElementVNode("form", null, [
        _createVNode(FormField, {
          "validation-state": _unref(validationState).result.value.password,
          "label-key": "labels.user.password"
        }, {
          default: _withCtx(({ onBlur }) => [
            _createVNode(_component_p_input_text, {
              modelValue: password.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((password).value = $event)),
              class: "w-full",
              type: "password",
              autofocus: "",
              onBlur: onBlur
            }, null, 8, ["modelValue", "onBlur"])
          ]),
          _: 1
        }, 8, ["validation-state"])
      ])
    ]),
    _: 1
  }, 8, ["visible"]))
}
}

})